import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { Wizard } from "@/components/ui/wizard";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { toast } from "@/components/ui/toast";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { useAutoCollapsedSidebar } from "@/hooks/use-auto-collapsed-sidebar";
import { getApiErrorMessages } from "@/utils/api-error";
import {
  AthleteCreationSchema,
  type AthleteCreationFormData,
} from "@/schemas/athlete";
import { useAthleteWizardSteps } from "@/components/create-athlete/use-athlete-wizard-steps";
import { athleteFormToRegisterPayload } from "@/components/create-athlete/athlete-payload";
import { ID_DOCUMENT_TYPE } from "@/constants/athlete";
import { useCreateAthlete } from "@/queries/use-athletes";
import { useGymStore } from "@/stores/gym-store";

export const Route = createFileRoute("/(protected)/athletes/new/")({
  component: RouteComponent,
});

const DEFAULT_VALUES: AthleteCreationFormData = {
  avatar: "",
  firstName: "",
  lastName: "",
  email: "",
  birthdate: "",
  phoneNumber: "",
  landlineNumber: "",
  gender: "",
  maritalStatus: "",
  nationality: "",
  idDocumentType: ID_DOCUMENT_TYPE.CitizenCard,
  idDocumentNumber: "",
  idDocumentExpirationDate: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  emergencyContactName: "",
  emergencyContactPhoneNumber: "",
  emergencyContactRelationship: "",
  medicalConditions: "",
  allergiesIntolerances: "",
  currentMedication: "",
  fiscalFullName: "",
  taxId: "",
  iban: "",
  billingAddressSameAsMain: true,
  billingAddressLine1: "",
  billingAddressLine2: "",
  billingCity: "",
  billingPostalCode: "",
};

function RouteComponent() {
  useAutoCollapsedSidebar();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedGym } = useGymStore();
  const { mutate: createAthlete, isPending } = useCreateAthlete();

  const form = useForm<AthleteCreationFormData>({
    resolver: zodResolver(AthleteCreationSchema()),
    mode: "onSubmit",
    defaultValues: DEFAULT_VALUES,
  });

  const steps = useAthleteWizardSteps();

  const navigateBack = () => {
    navigate({ to: PROTECTED_ROUTES.ATHLETES });
  };

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: true,
    isDirty: form.formState.isDirty,
    onLeave: navigateBack,
  });

  const handleComplete = (data: AthleteCreationFormData) => {
    if (!selectedGym?.id) {
      toast.error(t("pages:athletes.wizard.toaster.error.title"), {
        description: t("pages:athletes.wizard.toaster.error.description"),
      });
      return;
    }

    const payload = athleteFormToRegisterPayload(data, selectedGym.id);

    createAthlete(payload, {
      onSuccess: () => {
        toast.success(t("pages:athletes.wizard.toaster.success.title"), {
          description: t("pages:athletes.wizard.toaster.success.description"),
        });
        navigateBack();
      },
      onError: (error) => {
        toast.error(t("pages:athletes.wizard.toaster.error.title"), {
          description: getApiErrorMessages(error),
        });
      },
    });
  };

  const handleCancel = () => {
    handleCancelEdit(navigateBack);
  };

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>{t("pages:athletes.wizard.title")}</MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        <Wizard
          form={form}
          steps={steps}
          onComplete={handleComplete}
          isSubmitting={isPending}
          onCancel={handleCancel}
        />
      </PageContainer>
      <ConfirmDialog {...unsavedChangesDialogProps} />
    </div>
  );
}
